// React component - VideoGenerator.jsx
import React, { useState } from 'react';

const API_BASE_URL = 'http://localhost:5000/api';

function VideoGenerator() {
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(8);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState('720p');
  const [imageFile, setImageFile] = useState(null);
  const [mode, setMode] = useState('text'); // 'text' or 'image'
  const [loading, setLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);

  const generateTextToVideo = async () => {
    const response = await fetch(`${API_BASE_URL}/generate/text-to-video`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt,
        durationSeconds: duration,
        aspectRatio,
        resolution,
        numberOfVideos: 1
      })
    });
    return response.json();
  };

  const generateImageToVideo = async () => {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('prompt', prompt);
    formData.append('durationSeconds', duration);
    formData.append('aspectRatio', aspectRatio);

    const response = await fetch(`${API_BASE_URL}/generate/image-to-video`, {
      method: 'POST',
      body: formData
    });
    return response.json();
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    if (mode === 'image' && !imageFile) {
      setError('Please select an image');
      return;
    }

    setLoading(true);
    setError(null);
    setVideoUrl(null);

    try {
      const result = mode === 'text' 
        ? await generateTextToVideo() 
        : await generateImageToVideo();

      if (result.error) throw new Error(result.error);
      
      if (result.videos && result.videos.length > 0) {
        // Note: You'll need to make GCS URIs publicly accessible or generate signed URLs
        setVideoUrl(result.videos[0].gcsUri);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Veo Video Generator</h1>
      
      {/* Mode Selection */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setMode('text')}
          className={`px-4 py-2 rounded ${mode === 'text' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
        >
          Text to Video
        </button>
        <button
          onClick={() => setMode('image')}
          className={`px-4 py-2 rounded ${mode === 'image' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
        >
          Image to Video
        </button>
      </div>

      {/* Image Upload (for image mode) */}
      {mode === 'image' && (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Upload Image</label>
          <input
            type="file"
            accept="image/jpeg,image/png"
            onChange={(e) => setImageFile(e.target.files[0])}
            className="w-full p-2 border rounded"
          />
        </div>
      )}

      {/* Prompt */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Prompt</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="w-full p-3 border rounded h-32"
          placeholder="Describe what should happen in the video..."
        />
      </div>

      {/* Parameters */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium mb-2">Duration</label>
          <select
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="w-full p-2 border rounded"
          >
            <option value={4}>4 seconds</option>
            <option value={6}>6 seconds</option>
            <option value={8}>8 seconds</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Aspect Ratio</label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="16:9">16:9</option>
            <option value="9:16">9:16</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Resolution</label>
          <select
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="720p">720p ($0.05/sec)</option>
            <option value="1080p">1080p ($0.08/sec)</option>
          </select>
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={loading}
        className="w-full bg-blue-600 text-white py-3 rounded font-medium disabled:bg-gray-400"
      >
        {loading ? 'Generating... (1-2 minutes)' : 'Generate Video'}
      </button>

      {/* Error Display */}
      {error && (
        <div className="mt-6 p-4 bg-red-100 text-red-700 rounded">
          Error: {error}
        </div>
      )}

      {/* Video Result */}
      {videoUrl && (
        <div className="mt-6">
          <h3 className="font-medium mb-2">Generated Video:</h3>
          <video src={videoUrl} controls className="w-full rounded" />
          <p className="text-sm text-gray-500 mt-2">
            Note: Video is stored in Google Cloud Storage. Make sure it's publicly accessible.
          </p>
        </div>
      )}

      {/* Cost Estimate */}
      <div className="mt-6 p-3 bg-gray-100 rounded text-sm">
        Estimated cost: ${(duration * 0.05).toFixed(2)} for this video
      </div>
    </div>
  );
}

export default VideoGenerator;